import { useEffect, useState } from "react";
import { api } from "../../lib/api";

/* GATE HEATMAP — which gate blocks this symbol most over N days, and what the blocked trades did in the next 30 min. */
const short = (g) => g.replace(" (4H trend · wedge · liquidity · location)", "").replace(" / flip guard", "").replace(" / stacking / max positions", " / stacking");
const rTone = (r) => (r == null ? "var(--r2-text2)" : r > 0 ? "var(--r2-emerald)" : r < 0 ? "var(--r2-red)" : "var(--r2-text1)");

const Cf = ({ cf, testid }) => {
  if (!cf?.scored) return <span className="text-[var(--r2-text2)]" data-testid={testid}>not scored yet</span>;
  return (
    <span data-testid={testid} title={`${cf.scored} blocked triggers scored on the 30-min counterfactual`}>
      <span style={{ color: "var(--r2-emerald)" }}>{cf.win}W</span> <span style={{ color: "var(--r2-red)" }}>{cf.loss}L</span> <span className="text-[var(--r2-text2)]">{cf.open}o</span>
      {" · "}<b className="r2-num" style={{ color: rTone(cf.avg_r) }}>{cf.avg_r > 0 ? "+" : ""}{cf.avg_r}R</b>
      {cf.usd_1ct != null && <span className="text-[var(--r2-text2)]"> · {cf.usd_1ct >= 0 ? "+" : "-"}${Math.abs(cf.usd_1ct).toFixed(0)}/ct</span>}
    </span>
  );
};

export const GateHeatmap = ({ symbol, onSymbol }) => {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(7);
  useEffect(() => {
    let alive = true;
    api.gateHeatmap(symbol, days).then((r) => alive && setD(r)).catch(() => alive && setD({ error: true }));
    return () => { alive = false; };
  }, [symbol, days]);
  const gates = d?.gates || [];
  const max = Math.max(1, ...gates.map((g) => g.triggers));
  const t = d?.totals || {};
  return (
    <div className="mb-3 rounded-sm border border-[var(--r2-border)] p-2" data-testid="gates-heatmap">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[7px] font-bold tracking-[0.2em] text-[var(--r2-text2)]">BLOCKERS · {days}D · {symbol}</div>
        <div className="flex items-center gap-1">
          {(d?.symbols || [symbol]).map((s) => (
            <button key={s} onClick={() => onSymbol(s)} data-testid={`heatmap-sym-${s}`} className={`rounded-sm border px-1 py-[1px] text-[7px] font-bold tracking-[0.12em] ${s === symbol ? "border-[var(--r2-cyan)] text-[var(--r2-cyan)]" : "border-[var(--r2-border)] text-[var(--r2-text2)] hover:text-[var(--r2-text1)]"}`}>{s}</button>
          ))}
          <span className="mx-1 text-[var(--r2-border)]">|</span>
          {[1, 7, 30].map((n) => (
            <button key={n} onClick={() => setDays(n)} data-testid={`heatmap-days-${n}`} className={`rounded-sm border px-1 py-[1px] text-[7px] font-bold ${n === days ? "border-[var(--r2-text1)] text-[var(--r2-text1)]" : "border-[var(--r2-border)] text-[var(--r2-text2)]"}`}>{n}D</button>
          ))}
        </div>
      </div>
      {!d && <div className="text-[var(--r2-text2)]">loading…</div>}
      {d && gates.length === 0 && <div className="text-[var(--r2-text2)]" data-testid="heatmap-empty">no blocked triggers for {symbol} in the last {days} days</div>}
      {gates.map((g, i) => (
        <div key={g.gate} className="grid items-center gap-2 border-t border-[var(--r2-border)] py-[3px]" style={{ gridTemplateColumns: "170px minmax(80px,1fr) 150px 210px" }} data-testid={`heatmap-row-${i}`}>
          <span className="truncate text-[var(--r2-text1)]" title={g.gate}>{short(g.gate)}</span>
          <div className="flex items-center gap-1">
            <div className="h-[7px] rounded-sm bg-[var(--r2-red)]" style={{ width: `${Math.max(3, (g.triggers / max) * 100)}%`, opacity: 0.75 }} />
            <span className="r2-num whitespace-nowrap text-[var(--r2-text1)]" data-testid={`heatmap-count-${i}`}>{g.triggers} <span className="text-[var(--r2-text2)]">· {g.ticks} ticks</span></span>
          </div>
          <span className="truncate text-[var(--r2-text2)]" title="grades · sides blocked">
            {Object.entries(g.grades).sort().map(([k, v]) => `${k} ${v}`).join(" · ")} <span className="text-[var(--r2-border)]">|</span> {Object.entries(g.sides).map(([k, v]) => `${k[0]} ${v}`).join(" / ")}
          </span>
          <Cf cf={g.cf} testid={`heatmap-cf-${i}`} />
        </div>
      ))}
      {d && (
        <div className="mt-1 flex flex-wrap gap-x-3 text-[var(--r2-text2)]" data-testid="heatmap-totals">
          <span>triggers {t.triggers}</span><span>blocked {t.blocked}</span><span>sent {t.sent}</span>
          {t.sent_scored > 0 && <span>sent trades: {t.sent_win}/{t.sent_scored} won · <b style={{ color: rTone(t.sent_avg_r) }}>{t.sent_avg_r > 0 ? "+" : ""}{t.sent_avg_r}R</b></span>}
          <span className="basis-full text-[7px] opacity-70">would-have-won = signal entry / stop / target, next 30 min on 5m bars, adverse extreme first — a gate whose blocked trades average negative R is earning its keep</span>
        </div>
      )}
    </div>
  );
};
